import * as React from 'react';
import classNames from 'classnames';
import Option, { OptionProps } from './option';
import './style.scss';

const Icon: React.FC<{ type?: string; className?: string }> = (props) => {
  return <i {...props} />;
};

const PREFIX = 'ui-mobile-select';

export interface SelectProps {
  /** Options配置 */
  options?: OptionProps[];
  /** 默认选中的value */
  defaultValue?: string | number;
  /** 没有选中时的占位文案 */
  placeholder?: string;
  /** 不传options时需要将Option组件传入children中 */
  children?: any;
  /** onChange回调函数，将value和label传入 */
  onChange?: (value: any, label: any) => void;
  /** 是否展示遮罩层，默认展示 */
  showMask?: boolean;
  /** （受控）当前选中的value值 */
  value?: string | number;
  /** 选中的icon，默认不显示 */
  selectedIcon?: any;
  /** 下拉按钮箭头未展开时icon，默认不显示 */
  arrowDownIcon?: any;
  /** 下拉按钮箭头展开时icon，默认不显示 */
  arrowExpandIcon?: any;
}
export interface SelectStates {
  isOpen: boolean;
  selected: string | number;
  showText: any;
}
export class Select extends React.Component<SelectProps, SelectStates> {
  static defaultProps = {
    options: null,
    defaultValue: '',
    placeholder: '',
    children: null,
    onChange: () => {},
    showMask: true,
    value: '',
    selectedIcon: <Icon type="ic_select_right" />,
    arrowDownIcon: <Icon type="ic_arrow_down" className="arrow-down-icon" />,
    arrowExpandIcon: <Icon type="ic_arrow_down" className="arrow-expand-icon" />,
  };
  static Option: typeof Option;
  constructor(props: SelectProps) {
    super(props);

    this.state = {
      isOpen: false,
      selected: props.defaultValue || props.value || '',
      showText: this.getShowText(props),
    };
  }

  // 对外暴露的可以手动关闭选项框的方法
  close = () => {
    this.setState({
      isOpen: false,
    });
  };

  componentWillReceiveProps(nextProps: SelectProps) {
    if (nextProps.value && nextProps.value !== this.state.selected) {
      this.setState({
        selected: nextProps.value,
        showText: this.getShowText(nextProps),
      });
    }
  }

  /**
   * 获取初始showText
   * @param {SelectProps} props
   * @returns
   * @memberof Select
   */
  getShowText(props: SelectProps) {
    let showText = props.placeholder || '';
    const value = props.value || props.defaultValue;
    // 如果有初始默认value，则不显示placeholder直接显示对应的label
    if (value) {
      // 通过options的方式声明的子节点
      if (props.options) {
        const idx = props.options.findIndex((item) => item.value === value);
        if (idx > -1) {
          showText = props.options[idx].label;
        }
      } else {
        // 通过<Option>组件的方式声明的子节点
        if (Array.isArray(props.children)) {
          const idx = props.children.findIndex((item) => item.props.value === value);
          if (idx > -1) {
            showText = props.children[idx].props.label || props.children[idx].props.children;
          }
        } else {
          if (props.children.props.value === value) {
            showText = props.children.props.label || props.children.props.chilren;
          }
        }
      }
    }
    return showText;
  }
  /**
   * 切换isOpen状态
   */
  toggleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({
      isOpen: !this.state.isOpen,
    });
  };

  /**
   * 点击option时，改变当前selected值，并关闭选项栏
   * 如果props中有onChange事件，将value和label传入onChange以供外部组件调用
   */
  clickItem = (value: string, label: any) => {
    const { onChange } = this.props;
    this.setState({
      selected: value,
      showText: label,
      isOpen: false,
    });
    onChange && onChange(value, label);
  };

  /**
   * 创造子节点，如果有children则给children添加属性和点击事件，否则直接生成新的子节点
   * @memberof Select
   */
  createChildren() {
    let { children, options, selectedIcon } = this.props;
    const { selected } = this.state;
    if (options && options.length > 0) {
      children = options.map((item: OptionProps) => {
        return (
          <Option
            value={item.value}
            selected={selected}
            key={item.value}
            onClick={this.clickItem}
            selectedIcon={selectedIcon}
          >
            {item.label}
          </Option>
        );
      });
    } else {
      if (Array.isArray(children)) {
        children = children.map((item: any) => {
          return React.cloneElement(item, {
            onClick: this.clickItem,
            key: item.value,
            selected,
            selectedIcon,
          });
        });
      }
    }
    return children;
  }
  render() {
    const children = this.createChildren();
    const { isOpen, showText } = this.state;
    const { showMask, arrowDownIcon, arrowExpandIcon } = this.props;
    return (
      <div className={classNames([`${PREFIX}-container`, { open: isOpen }])}>
        {showMask && <div className={`${PREFIX}-mask`} onClick={this.toggleOpen} />}
        <div className={`${PREFIX}-body`}>
          <div className={`${PREFIX}-text-box`} onClick={this.toggleOpen}>
            <strong className="show-text">{showText}</strong>
            {isOpen ? arrowExpandIcon : arrowDownIcon}
          </div>
          <div className={`${PREFIX}-list-box`}>
            <ul className={`${PREFIX}-item-list`}>{children}</ul>
          </div>
        </div>
      </div>
    );
  }
}

Select.Option = Option;
